import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { Button, ElasticFormModal } from 'basic-components';
import { DataLakeEnums, DataLakeQuery } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { globalActions, statusBoxActions } from 'primary-components';

import { ncsvCreatorActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { sendEvent } from 'services/GAService';

import Content from './Content';

import { useQueryClient } from '@tanstack/react-query';

import './NCSVCreator.scss';

const STEPS = ncsvCreatorActions.STEPS
const NCSVCreator = () => {
  const dispatch = useDispatch(),
        { id: fileId, onSave, rawFile, customOnSubmit } = useSelector(state => state.toolsState.ncsvCreatorState),
        [activeStep, setActiveStep] = useState(ncsvCreatorActions.STEPS.DATE),
        stepsArray = useMemo(() => Object.values(STEPS), []),
        contentRef = useRef(),
        close = useCallback(() => {
          batch(() => {
            dispatch(ncsvCreatorActions.changeValue(undefined, 'id'))
            dispatch(ncsvCreatorActions.changeValue(false, 'open'))
            dispatch(ncsvCreatorActions.changeValue(undefined, 'onSave'))
            dispatch(ncsvCreatorActions.changeValue({}, 'columnConfigs'))
            dispatch(ncsvCreatorActions.changeValue(undefined, 'rawFile'))
            dispatch(ncsvCreatorActions.changeValue(undefined, 'customOnSubmit'))
          })
        }, [dispatch]),
        getQueryBuilderUpload = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        invalidateRef = useRef(),
        queryClient = useQueryClient(),
        { uploadFileMetadata, uploadFileContent } = filesDatalakeQueries.useFileUpload({ getQueryBuilder: getQueryBuilderUpload, fileType: DataLakeEnums.FILE_TYPE.NCSV }),
        onSubmit = useCallback(() => {
          const fileInfo = contentRef.current?.getFileInfo()
          if(customOnSubmit) {
            return customOnSubmit(contentRef.current?.getAsCSV(), close, fileInfo?.fileName)
          }
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return uploadFileMetadata({
              fileName: fileInfo?.fileName, 
              groupName: fileInfo?.groupName,
              fields: Object.entries(fileInfo.fields).map(([k, v]) => ({ name: k, value: v }))
            })
            .then(response => {
              const jobId = response.headers.get("X-KH-JOB-ID")
              dispatch(statusBoxActions.addJob({ jobId, displayName: fileInfo.fileName, subtitle: fileInfo.groupName }))
              return response
            })
            .then(response => response.json())
            .then(response => uploadFileContent({
              url: response.location,
              image: contentRef.current?.getAsCSV()
            }))
            .then(() => dispatch(globalActions.getMessageStore()).success("NCSV file has been saved."))
            .then(() => {
              close()
              onSave()
              clearTimeout(invalidateRef.current)
              invalidateRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000)
            })
            .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
        }, [customOnSubmit, dispatch, uploadFileMetadata, close, uploadFileContent, onSave, queryClient]),
        formRef = useRef(),
        changeSteps = useCallback(forward => setActiveStep(currentStep => {
          let currentIndex = stepsArray.findIndex(value => value === currentStep)
          if(forward && currentIndex < stepsArray.length - 1) {
            currentIndex++
          } else if(currentIndex > 0) {
            currentIndex -= 1
          }
          return stepsArray[currentIndex]
        }), [stepsArray]),
        nextStep = useCallback(() => changeSteps(true), [changeSteps]),
        prevStep = useCallback(() => changeSteps(false), [changeSteps]),
        setActiveStepWithValid = useCallback(step => {
          if(formRef.current.validate()) {
            const nextIndex = stepsArray.findIndex(value => value === step)
            const currentIndex = stepsArray.findIndex(value => value === activeStep)
            if(nextIndex > currentIndex) {
              setActiveStep(step)  
            } else if(nextIndex < currentIndex) {
              setActiveStep(step)
            }
          }
        }, [activeStep, stepsArray]),

        button = useCallback(() => (
          <Fragment key="fragment">
            <Button
                onClick={prevStep}
                size="small"
                type="button"
                variant="secondary"
            >
              Back
            </Button>
          </Fragment>
        ), [prevStep])

  useEffect(() => () => {
    close()
  }, [close])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "ncsv_creator",
      label: "tool",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  return (
    <ElasticFormModal
        buttons={button}
        className="ncsv-creator"
        closeOnBackground={false}
        closeOnEsc={false}
        onClose={close}
        onSubmit={activeStep === STEPS.VALIDATE ? onSubmit : nextStep}
        open
        ref={formRef}
        submitText={activeStep === STEPS.VALIDATE ? "Save" : "Next"}
        tabIndex="0"
        title="NCSV Creator"
    >
      <Content
          activeStep={activeStep}
          fileId={fileId}
          onClose={close}
          rawFile={rawFile}
          ref={contentRef}
          setActiveStep={setActiveStepWithValid}
      />
    </ElasticFormModal>
  )
}

export default memo(NCSVCreator)